/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

/**
 * J2 homepage header
 *
 * - Transparent gradient over the homepage video
 * - Progressively becomes solid black while scrolling
 * - Becomes fully solid after 450px
 * - Becomes solid black while the mobile menu drawer is open
 * - Returns to the correct scroll opacity after the drawer closes
 */
(() => {
  const initialiseHomepageHeader = () => {
    const header = document.querySelector(
      'store-header.header--homepage-overlay'
    );

    if (!header) return;

    /*
     * Prevent duplicate listeners when Shopify reloads the header
     * inside the theme editor.
     */
    if (header.dataset.j2OverlayInitialised === 'true') {
      return;
    }

    header.dataset.j2OverlayInitialised = 'true';

    const mobileMenuDisclosure = header.querySelector(
      '.main-menu__disclosure'
    );

    /*
     * The theme switches to desktop navigation at 769px.
     */
    const mobileBreakpoint = window.matchMedia(
      '(max-width: 768.98px)'
    );

    /*
     * Scroll distance at which the header becomes completely black.
     *
     * Increase this number for an even slower transition.
     */
    const solidAfter = 450;

    let animationFrame = null;

    const getScrollPosition = () => (
      window.scrollY ||
      document.documentElement.scrollTop ||
      0
    );

    const updateScrollState = () => {
      const scrollPosition = getScrollPosition();

      /*
       * Convert the scroll position into a value between 0 and 1.
       */
      const linearProgress = Math.min(
        Math.max(scrollPosition / solidAfter, 0),
        1
      );

      /*
       * Squaring the progress keeps the header more transparent
       * near the top and makes the transition build more gradually.
       */
      const opacityProgress =
        linearProgress * linearProgress;

      header.style.setProperty(
        '--j2-header-solid-progress',
        opacityProgress.toFixed(3)
      );

      /*
       * Add the final solid state when the transition is complete.
       */
      header.classList.toggle(
        'is-solid',
        linearProgress >= 1
      );

      animationFrame = null;
    };

    const requestScrollUpdate = () => {
      if (animationFrame !== null) return;

      animationFrame = window.requestAnimationFrame(
        updateScrollState
      );
    };

    const updateMobileMenuState = () => {
    const mobileMenuIsOpen = Boolean(
        mobileBreakpoint.matches &&
        mobileMenuDisclosure.classList.contains('is-open')
    );

    const headerWasForcedSolid = header.classList.contains(
        'is-mobile-menu-open'
    );

    if (mobileMenuIsOpen) {
        header.classList.remove(
        'is-mobile-menu-resetting'
        );

        header.classList.add(
        'is-mobile-menu-open'
        );

        return;
    }

    /*
    * The theme removes .is-open immediately when closing.
    * Disable transitions before removing the forced-black state.
    */
    if (headerWasForcedSolid) {
        header.classList.add(
        'is-mobile-menu-resetting'
        );

        header.classList.remove(
        'is-mobile-menu-open'
        );

        /*
        * Force the browser to apply the transparent/scroll opacity
        * immediately without animating toward it.
        */
        void header.offsetWidth;

        window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
            header.classList.remove(
            'is-mobile-menu-resetting'
            );
        });
        });
    }
    };

    if (mobileMenuDisclosure) {
    const mobileMenuObserver = new MutationObserver(
        updateMobileMenuState
    );

    /*
    * Watch the class instead of the open attribute.
    * The open attribute remains during the closing animation,
    * while is-open disappears immediately.
    */
    mobileMenuObserver.observe(
        mobileMenuDisclosure,
        {
        attributes: true,
        attributeFilter: ['class']
        }
    );

    if (typeof mobileBreakpoint.addEventListener === 'function') {
        mobileBreakpoint.addEventListener(
        'change',
        updateMobileMenuState
        );
    } else {
        mobileBreakpoint.addListener(
        updateMobileMenuState
        );
    }

    updateMobileMenuState();
    }

    updateScrollState();

    window.addEventListener(
      'scroll',
      requestScrollUpdate,
      { passive: true }
    );

    /*
     * Recalculate after browser back/forward navigation.
     */
    window.addEventListener(
      'pageshow',
      updateScrollState
    );
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialiseHomepageHeader,
      { once: true }
    );
  } else {
    initialiseHomepageHeader();
  }

  /*
   * Support header reloads in the Shopify theme editor.
   */
  document.addEventListener(
    'shopify:section:load',
    initialiseHomepageHeader
  );
})();

(() => {
  'use strict';

  const CONFIG = {
    button: '.j2-cart-pdf-link',
    jsPdfUrl:
      'https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js',

    shopName: 'J2 Scooters & Motoren',
    locations: 'Webshop',
    website: 'www.j2scooters.nl',

    logoSelector:
      '.header__logo .logo__link img',

    logoFallback:
      'https://cdn.shopify.com/s/files/1/0626/0804/8364/files/logo_small_d8401e46-abc7-437e-af72-a96a5f255f4e.png'
  };

  let loadingPdf = null;
  let generating = false;

  const clean = (value) =>
    String(value ?? '')
      .replace(/<[^>]*>/g, '')
      .replace(/&amp;/gi, '&')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const cents = (value) => {
    const number = Number(value || 0);

    return Number.isFinite(number)
      ? Math.round(number)
      : 0;
  };

  const money = (
    value,
    currency = 'EUR'
  ) => {
    try {
      return new Intl.NumberFormat(
        'nl-NL',
        {
          style: 'currency',
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }
      ).format(cents(value) / 100);
    } catch (error) {
      return `€ ${(
        cents(value) / 100
      )
        .toFixed(2)
        .replace('.', ',')}`;
    }
  };

  const formatDate = (date) =>
    new Intl.DateTimeFormat('nl-NL', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);

  const reference = (date) => {
    const two = (number) =>
      String(number).padStart(2, '0');

    return [
      'J2',
      `${date.getFullYear()}${two(
        date.getMonth() + 1
      )}${two(date.getDate())}`,
      `${two(date.getHours())}${two(
        date.getMinutes()
      )}${two(date.getSeconds())}`
    ].join('-');
  };

  function setBusy(button, busy) {
    if (!button) return;

    if (busy) {
      button.dataset.originalText ||=
        clean(button.textContent);

      button.textContent =
        'PDF wordt gemaakt…';

      button.classList.add(
        'is-loading'
      );

      button.setAttribute(
        'aria-busy',
        'true'
      );
    } else {
      button.textContent =
        button.dataset.originalText ||
        'Winkelwagen downloaden als PDF';

      button.classList.remove(
        'is-loading'
      );

      button.removeAttribute(
        'aria-busy'
      );
    }
  }

  function loadJsPdf() {
    if (window.jspdf?.jsPDF) {
      return Promise.resolve(
        window.jspdf.jsPDF
      );
    }

    if (loadingPdf) {
      return loadingPdf;
    }

    loadingPdf = new Promise(
      (resolve, reject) => {
        const existingScript =
          document.querySelector(
            'script[data-j2-jspdf]'
          );

        const finish = () => {
          if (window.jspdf?.jsPDF) {
            resolve(
              window.jspdf.jsPDF
            );
          } else {
            reject(
              new Error(
                'De PDF-bibliotheek is niet beschikbaar.'
              )
            );
          }
        };

        if (existingScript) {
          existingScript.addEventListener(
            'load',
            finish,
            {
              once: true
            }
          );

          existingScript.addEventListener(
            'error',
            () => {
              reject(
                new Error(
                  'De PDF-bibliotheek kon niet worden geladen.'
                )
              );
            },
            {
              once: true
            }
          );

          return;
        }

        const script =
          document.createElement(
            'script'
          );

        script.src = CONFIG.jsPdfUrl;
        script.async = true;
        script.dataset.j2Jspdf =
          'true';

        script.addEventListener(
          'load',
          finish,
          {
            once: true
          }
        );

        script.addEventListener(
          'error',
          () => {
            reject(
              new Error(
                'De PDF-bibliotheek kon niet worden geladen.'
              )
            );
          },
          {
            once: true
          }
        );

        document.head.appendChild(
          script
        );
      }
    ).catch((error) => {
      loadingPdf = null;
      throw error;
    });

    return loadingPdf;
  }

  async function getCart() {
    const root =
      window.Shopify?.routes?.root ||
      '/';

    const response = await fetch(
      `${root}cart.js`,
      {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        'De winkelwagen kon niet worden opgehaald.'
      );
    }

    return response.json();
  }

  async function addCompareAtPrices(
    cart
  ) {
    const root =
      window.Shopify?.routes?.root ||
      '/';

    const handles = [
      ...new Set(
        (cart.items || [])
          .map((item) =>
            clean(item.handle)
          )
          .filter(Boolean)
      )
    ];

    const results =
      await Promise.allSettled(
        handles.map(
          async (handle) => {
            const response =
              await fetch(
                `${root}products/${encodeURIComponent(
                  handle
                )}.js`,
                {
                  credentials:
                    'same-origin',
                  cache: 'no-store',
                  headers: {
                    Accept:
                      'application/json'
                  }
                }
              );

            if (!response.ok) {
              throw new Error(handle);
            }

            return {
              handle,
              product:
                await response.json()
            };
          }
        )
      );

    const products = new Map();

    results.forEach((result) => {
      if (
        result.status ===
        'fulfilled'
      ) {
        products.set(
          result.value.handle,
          result.value.product
        );
      }
    });

    (cart.items || []).forEach(
      (item) => {
        const product =
          products.get(
            clean(item.handle)
          );

        const variant =
          product?.variants?.find(
            (candidate) =>
              Number(candidate.id) ===
              Number(
                item.variant_id ||
                  item.id
              )
          );

        const compareAt = cents(
          variant?.compare_at_price
        );

        const original = cents(
          item.original_price ??
            item.price ??
            item.final_price
        );

        item._j2CompareAtPrice =
          compareAt > original
            ? compareAt
            : 0;
      }
    );
  }

  function loadLogo() {
    const element =
      document.querySelector(
        CONFIG.logoSelector
      );

    const source =
      CONFIG.logoFallback ||
      element?.src ||
      element?.currentSrc ||
      '';

    if (!source) {
      return Promise.resolve(null);
    }

    return new Promise((resolve) => {
      const image = new Image();

      image.crossOrigin =
        'anonymous';

      image.decoding = 'async';

      image.onload = () => {
        try {
          const width =
            image.naturalWidth ||
            image.width;

          const height =
            image.naturalHeight ||
            image.height;

          if (!width || !height) {
            resolve(null);
            return;
          }

          const canvas =
            document.createElement(
              'canvas'
            );

          canvas.width = width;
          canvas.height = height;

          const context =
            canvas.getContext('2d', {
              willReadFrequently: true
            });

          if (!context) {
            resolve(null);
            return;
          }

          context.clearRect(
            0,
            0,
            width,
            height
          );

          context.drawImage(
            image,
            0,
            0,
            width,
            height
          );

          const imageData =
            context.getImageData(
              0,
              0,
              width,
              height
            );

          const pixels =
            imageData.data;

          let minX = width;
          let minY = height;
          let maxX = -1;
          let maxY = -1;

          for (
            let y = 0;
            y < height;
            y += 1
          ) {
            for (
              let x = 0;
              x < width;
              x += 1
            ) {
              const index =
                (y * width + x) * 4;

              const alpha =
                pixels[index + 3];

              if (alpha < 12) {
                continue;
              }

              minX = Math.min(
                minX,
                x
              );

              minY = Math.min(
                minY,
                y
              );

              maxX = Math.max(
                maxX,
                x
              );

              maxY = Math.max(
                maxY,
                y
              );

              /*
               * Make every visible part
               * of the logo white.
               */
              pixels[index] = 255;
              pixels[index + 1] = 255;
              pixels[index + 2] = 255;
            }
          }

          if (
            maxX < minX ||
            maxY < minY
          ) {
            resolve(null);
            return;
          }

          context.putImageData(
            imageData,
            0,
            0
          );

          const cropWidth =
            maxX - minX + 1;

          const cropHeight =
            maxY - minY + 1;

          const cropped =
            document.createElement(
              'canvas'
            );

          cropped.width = cropWidth;
          cropped.height =
            cropHeight;

          const croppedContext =
            cropped.getContext('2d');

          if (!croppedContext) {
            resolve(null);
            return;
          }

          croppedContext.drawImage(
            canvas,
            minX,
            minY,
            cropWidth,
            cropHeight,
            0,
            0,
            cropWidth,
            cropHeight
          );

          resolve({
            data:
              cropped.toDataURL(
                'image/png'
              ),
            width: cropWidth,
            height: cropHeight
          });
        } catch (error) {
          console.warn(
            '[J2 Cart PDF] Logo overgeslagen.',
            error
          );

          resolve(null);
        }
      };

      image.onerror = () => {
        resolve(null);
      };

      image.src = new URL(
        source,
        window.location.href
      ).href;
    });
  }

  function propertyLabel(key) {
    const labels = {
      '_Gekozen opties':
        'Gekozen opties',

      '_Configuratie per stap':
        'Configuratie',

      '_Hoort bij':
        'Gekozen bij',

      Inbegrepen:
        'Inbegrepen'
    };

    return (
      labels[key] ||
      clean(key)
        .replace(/^_+/, '')
        .replace(/_/g, ' ')
        .replace(
          /^./,
          (letter) =>
            letter.toUpperCase()
        )
    );
  }

  function getProperties(item) {
    const properties =
      item.properties || {};

    const lines = [];
    const seen = new Set();

    const included = clean(
      properties.Inbegrepen
    );

    const hasSteps = Boolean(
      clean(
        properties[
          '_Configuratie per stap'
        ]
      )
    );

    const add = (line) => {
      line = clean(line);

      if (!line) return;

      const key =
        line.toLocaleLowerCase(
          'nl-NL'
        );

      if (seen.has(key)) return;

      seen.add(key);
      lines.push(line);
    };

    Object.entries(
      properties
    ).forEach(
      ([rawKey, rawValue]) => {
        const key = clean(rawKey);
        const value =
          clean(rawValue);

        const lowerValue =
          value.toLocaleLowerCase(
            'nl-NL'
          );

        if (!key || !value) return;

        if (
          lowerValue === 'geen' ||
          lowerValue ===
            'geen extra opties'
        ) {
          return;
        }

        if (
          key.startsWith('__')
        ) {
          return;
        }

        if (
          key === '_Configuratie'
        ) {
          return;
        }

        /*
         * Do not show this beneath
         * each accessory.
         */
        if (
          key ===
          '_Configurator optie'
        ) {
          return;
        }

        if (
          key ===
            '_Gekozen opties' &&
          hasSteps
        ) {
          return;
        }

        if (
          key ===
          '_Configuratie per stap'
        ) {
          value
            .split('|')
            .forEach((part) => {
              part = clean(part);

              /*
               * Inbegrepen already has
               * its own property.
               */
              if (
                included &&
                /^inbegrepen\s*:/i.test(
                  part
                )
              ) {
                return;
              }

              add(part);
            });

          return;
        }

        const visibleValue =
          value.startsWith(
            '/uploads/'
          )
            ? 'bestand toegevoegd'
            : value;

        add(
          `${propertyLabel(
            key
          )}: ${visibleValue}`
        );
      }
    );

    return lines;
  }

  function discountTitle(value) {
    return clean(
      value?.title ||
        value?.description ||
        value
          ?.discount_application
          ?.title ||
        value
          ?.discount_application
          ?.description ||
        ''
    );
  }

  function itemDiscountNames(item) {
    const names = new Set();

    const allocations =
      item
        .line_level_discount_allocations
        ?.length
        ? item
            .line_level_discount_allocations
        : item
            .discount_allocations ||
          [];

    allocations.forEach(
      (allocation) => {
        const title =
          discountTitle(
            allocation
          );

        if (title) {
          names.add(title);
        }
      }
    );

    (
      item.discounts || []
    ).forEach((discount) => {
      const title =
        discountTitle(discount);

      if (title) {
        names.add(title);
      }
    });

    return [...names];
  }

  function cartDiscountNames(cart) {
    const names = new Set();

    (
      cart
        .cart_level_discount_applications ||
      []
    ).forEach((application) => {
      const title =
        discountTitle(
          application
        );

      if (title) {
        names.add(title);
      }
    });

    (cart.items || []).forEach(
      (item) => {
        itemDiscountNames(
          item
        ).forEach((title) => {
          names.add(title);
        });
      }
    );

    return [...names];
  }

  function priceSummary(cart) {
    let normal = 0;
    let compareAtDiscount = 0;

    (cart.items || []).forEach(
      (item) => {
        const quantity = Number(
          item.quantity || 0
        );

        const originalUnit =
          cents(
            item.original_price ??
              item.price ??
              item.final_price
          );

        const originalLine =
          cents(
            item.original_line_price ??
              originalUnit *
                quantity
          );

        const compareAtUnit =
          cents(
            item._j2CompareAtPrice
          );

        const compareAtLine =
          compareAtUnit >
          originalUnit
            ? compareAtUnit *
              quantity
            : 0;

        if (
          compareAtLine >
          originalLine
        ) {
          normal +=
            compareAtLine;

          compareAtDiscount +=
            compareAtLine -
            originalLine;
        } else {
          normal +=
            originalLine;
        }
      }
    );

    return {
      normal,

      compareAtDiscount,

      shopifyDiscount:
        cents(
          cart.total_discount
        ),

      total:
        cents(cart.total_price)
    };
  }

  function fitLine(
    doc,
    text,
    maxWidth
  ) {
    text = clean(text);

    if (
      doc.getTextWidth(text) <=
      maxWidth
    ) {
      return text;
    }

    while (
      text.length > 1 &&
      doc.getTextWidth(
        `${text}…`
      ) > maxWidth
    ) {
      text = text.slice(0, -1);
    }

    return `${text.trim()}…`;
  }

  function makePdf(
    cart,
    JsPDF,
    logo
  ) {
    const now = new Date();
    const ref = reference(now);

    const currency =
      cart.currency ||
      window.Shopify?.currency
        ?.active ||
      'EUR';

    const doc = new JsPDF({
      unit: 'mm',
      format: 'a4',
      compress: true
    });

    const pageWidth =
      doc.internal.pageSize.getWidth();

    const pageHeight =
      doc.internal.pageSize.getHeight();

    const left = 16;
    const right = 16;

    const contentWidth =
      pageWidth - left - right;

    const bottom =
      pageHeight - 18;

    let y = 45;

    const nextPage = () => {
      doc.addPage();

      doc.setFillColor(0, 0, 0);

      doc.rect(
        0,
        0,
        pageWidth,
        15,
        'F'
      );

      doc.setTextColor(
        255,
        255,
        255
      );

      doc.setFont(
        'helvetica',
        'bold'
      );

      doc.setFontSize(10);

      doc.text(
        `${CONFIG.shopName} Winkelwagenoverzicht`,
        left,
        10
      );

      doc.setTextColor(
        0,
        0,
        0
      );

      y = 24;
    };

    const space = (needed) => {
      if (
        y + needed >
        bottom
      ) {
        nextPage();
      }
    };

    const write = (
      text,
      options = {}
    ) => {
      const x =
        options.x ?? left;

      const maxWidth =
        options.maxWidth ??
        contentWidth;

      const size =
        options.size ?? 9;

      const lineHeight =
        options.lineHeight ??
        4.4;

      const style =
        options.style ??
        'normal';

      const color =
        options.color ??
        [0, 0, 0];

      doc.setFont(
        'helvetica',
        style
      );

      doc.setFontSize(size);

      doc.setTextColor(
        ...color
      );

      const lines =
        doc.splitTextToSize(
          clean(text),
          maxWidth
        );

      space(
        lines.length *
          lineHeight +
          1
      );

      doc.text(lines, x, y);

      y +=
        lines.length *
        lineHeight;
    };

    /*
     * Main header.
     */
    doc.setFillColor(0, 0, 0);

    doc.rect(
      0,
      0,
      pageWidth,
      36,
      'F'
    );

    let brandX = left;

    if (logo?.data) {
      try {
        const areaX = left;
        const areaY = 6;
        const areaWidth = 27;
        const areaHeight = 24;

        const ratio =
          logo.width /
          logo.height;

        let imageWidth =
          areaWidth;

        let imageHeight =
          imageWidth / ratio;

        if (
          imageHeight >
          areaHeight
        ) {
          imageHeight =
            areaHeight;

          imageWidth =
            imageHeight *
            ratio;
        }

        /*
         * No white rectangle.
         * The logo itself is white
         * and sits on the black header.
         */
        doc.addImage(
          logo.data,
          'PNG',

          areaX +
            (areaWidth -
              imageWidth) /
              2,

          areaY +
            (areaHeight -
              imageHeight) /
              2,

          imageWidth,
          imageHeight
        );

        brandX =
          areaX +
          areaWidth +
          7;
      } catch (error) {
        console.warn(
          '[J2 Cart PDF] Logo overgeslagen.',
          error
        );
      }
    }

    doc.setTextColor(
      255,
      255,
      255
    );

    doc.setFont(
      'helvetica',
      'bold'
    );

    doc.setFontSize(15);

    doc.text(
      CONFIG.shopName,
      brandX,
      15
    );

    doc.setFont(
      'helvetica',
      'normal'
    );

    doc.setFontSize(9);

    doc.text(
      CONFIG.locations,
      brandX,
      22
    );

    doc.text(
      CONFIG.website,
      brandX,
      28
    );

    doc.setFont(
      'helvetica',
      'bold'
    );

    doc.setFontSize(12);

    doc.text(
      'Winkelwagenoverzicht',
      pageWidth - right,
      15,
      {
        align: 'right'
      }
    );

    doc.setFont(
      'helvetica',
      'normal'
    );

    doc.setFontSize(8.5);

    // doc.text(
    //   `Referentie: ${ref}`,
    //   pageWidth - right,
    //   22,
    //   {
    //     align: 'right'
    //   }
    // );

    doc.text(
      formatDate(now),
      pageWidth - right,
      28,
      {
        align: 'right'
      }
    );

    write(
      `${cart.item_count} artikel${
        Number(
          cart.item_count
        ) === 1
          ? ''
          : 'en'
      } in de winkelwagen`,
      {
        color: [
          70,
          70,
          70
        ]
      }
    );

    y += 5;

    /*
     * Products.
     */
    (
      cart.items || []
    ).forEach(
      (item, index) => {
        const title = clean(
          item.product_title ||
            item.title ||
            'Product'
        );

        const variant = clean(
          item.variant_title
        );

        const quantity =
          Number(
            item.quantity ||
              0
          );

        const finalUnit =
          cents(
            item.final_price ??
              item.price
          );

        const originalUnit =
          cents(
            item.original_price ??
              item.price ??
              item.final_price
          );

        const finalLine =
          cents(
            item.final_line_price ??
              finalUnit *
                quantity
          );

        const originalLine =
          cents(
            item.original_line_price ??
              originalUnit *
                quantity
          );

        const compareAtUnit =
          cents(
            item._j2CompareAtPrice
          );

        const compareAtLine =
          compareAtUnit >
          originalUnit
            ? compareAtUnit *
              quantity
            : 0;

        const compareAtSaving =
          Math.max(
            0,
            compareAtLine -
              originalLine
          );

        const shopifySaving =
          Math.max(
            0,
            originalLine -
              finalLine
          );

        const discountNames =
          itemDiscountNames(
            item
          );

        const properties =
          getProperties(item);

        space(27);

        doc.setFillColor(
          245,
          245,
          245
        );

        doc.roundedRect(
          left,
          y - 4,
          contentWidth,
          9,
          1.5,
          1.5,
          'F'
        );

        doc.setTextColor(
          0,
          0,
          0
        );

        doc.setFont(
          'helvetica',
          'bold'
        );

        doc.setFontSize(
          10.5
        );

        const titleLines =
          doc.splitTextToSize(
            `${index + 1}. ${title}`,
            contentWidth - 50
          );

        doc.text(
          titleLines,
          left + 3,
          y + 2
        );

        doc.text(
          money(
            finalLine,
            currency
          ),
          pageWidth -
            right -
            3,
          y + 2,
          {
            align: 'right'
          }
        );

        y += Math.max(
          11,
          titleLines.length *
            4.8 +
            5
        );

        if (
          variant &&
          variant.toLowerCase() !==
            'default title'
        ) {
          write(
            `Uitvoering: ${variant}`,
            {
              x: left + 4,
              maxWidth:
                contentWidth -
                8,
              color: [
                65,
                65,
                65
              ]
            }
          );
        }

        write(
          `Aantal: ${quantity}  •  Prijs per stuk: ${money(
            finalUnit,
            currency
          )}`,
          {
            x: left + 4,
            maxWidth:
              contentWidth -
              8,
            color: [
              65,
              65,
              65
            ]
          }
        );

        /*
         * Compare-at price discount.
         */
        if (
          compareAtSaving > 0
        ) {
          write(
            `Normale prijs: ${money(
              compareAtLine,
              currency
            )}`,
            {
              x: left + 4,
              maxWidth:
                contentWidth -
                8,
              color: [
                90,
                90,
                90
              ]
            }
          );

          write(
            `Korting: -${money(
              compareAtSaving,
              currency
            )}`,
            {
              x: left + 4,
              maxWidth:
                contentWidth -
                8,
              style: 'bold'
            }
          );
        }

        /*
         * Shopify discount code or
         * automatic discount.
         */
        if (
          shopifySaving > 0
        ) {
          const label =
            discountNames.length
              ? `Korting – ${discountNames.join(
                  ', '
                )}`
              : 'Extra korting';

          write(
            `${label}: -${money(
              shopifySaving,
              currency
            )}`,
            {
              x: left + 4,
              maxWidth:
                contentWidth -
                8,
              style: 'bold'
            }
          );
        }

        if (
          properties.length
        ) {
          y += 1;

          write('Details', {
            x: left + 4,
            maxWidth:
              contentWidth -
              8,
            style: 'bold'
          });

          properties.forEach(
            (property) => {
              write(
                `• ${property}`,
                {
                  x: left + 8,

                  maxWidth:
                    contentWidth -
                    12,

                  size: 8.7,

                  lineHeight:
                    4.2,

                  color: [
                    45,
                    45,
                    45
                  ]
                }
              );
            }
          );
        }

        y += 5;

        space(4);

        doc.setDrawColor(
          220,
          220,
          220
        );

        doc.line(
          left,
          y,
          pageWidth - right,
          y
        );

        y += 8;
      }
    );

    /*
     * Totals.
     */
    const totals =
      priceSummary(cart);

    const names =
      cartDiscountNames(cart);

    const rows = [];

    if (
      totals.compareAtDiscount >
      0
    ) {
      rows.push({
        label:
          'Aanbiedingskorting',

        amount:
          totals.compareAtDiscount
      });
    }

    if (
      totals.shopifyDiscount >
      0
    ) {
      rows.push({
        label:
          names.length === 1
            ? `Korting – ${names[0]}`
            : names.length > 1
              ? 'Kortingscodes en automatische korting'
              : 'Extra korting',

        amount:
          totals.shopifyDiscount
      });
    }

    const rowHeight = 6;

    const boxHeight =
      34 +
      rows.length *
        rowHeight;

    space(boxHeight + 14);

    const boxY = y - 4;

    const summaryLeft =
      left + 4;

    const summaryRight =
      pageWidth -
      right -
      4;

    doc.setFillColor(
      248,
      248,
      248
    );

    doc.roundedRect(
      left,
      boxY,
      contentWidth,
      boxHeight,
      2,
      2,
      'F'
    );

    doc.setTextColor(
      0,
      0,
      0
    );

    doc.setFont(
      'helvetica',
      'bold'
    );

    doc.setFontSize(11);

    doc.text(
      'Overzicht',
      summaryLeft,
      boxY + 8
    );

    doc.setFont(
      'helvetica',
      'normal'
    );

    doc.setFontSize(9.5);

    doc.text(
      rows.length
        ? 'Normale prijs'
        : 'Subtotaal',

      summaryLeft,
      boxY + 16
    );

    doc.text(
      money(
        rows.length
          ? totals.normal
          : totals.total,

        currency
      ),

      summaryRight,
      boxY + 16,

      {
        align: 'right'
      }
    );

    rows.forEach(
      (row, index) => {
        const rowY =
          boxY +
          22 +
          index *
            rowHeight;

        doc.text(
          fitLine(
            doc,
            row.label,
            contentWidth - 65
          ),

          summaryLeft,
          rowY
        );

        doc.text(
          `-${money(
            row.amount,
            currency
          )}`,

          summaryRight,
          rowY,

          {
            align: 'right'
          }
        );
      }
    );

    const lineY =
      boxY +
      21 +
      rows.length *
        rowHeight;

    const totalY =
      lineY + 8;

    doc.setDrawColor(
      190,
      190,
      190
    );

    doc.line(
      summaryLeft,
      lineY,
      summaryRight,
      lineY
    );

    doc.setFont(
      'helvetica',
      'bold'
    );

    doc.setFontSize(11);

    /*
     * Label and amount use
     * exactly the same baseline.
     */
    doc.text(
      'Totaal',
      summaryLeft,
      totalY
    );

    doc.text(
      money(
        totals.total,
        currency
      ),

      summaryRight,
      totalY,

      {
        align: 'right'
      }
    );

    y =
      boxY +
      boxHeight +
      10;

    if (cart.note) {
      space(18);

      write(
        'Opmerking bij de winkelwagen',
        {
          style: 'bold'
        }
      );

      write(cart.note, {
        color: [
          60,
          60,
          60
        ]
      });

      y += 4;
    }

    /*
    * Important information and deposit instructions.
    */
    nextPage();
      /*
    * Extra witruimte onder de zwarte paginakop.
    */
    y += 10;


    write('Belangrijk', {
      size: 11,
      style: 'bold'
    });

    write(
      'Dit winkelwagenoverzicht is geen bindende offerte of reservering. Prijzen, kortingen, acties en beschikbaarheid kunnen wijzigen. Eventuele verzend-, aflever- of montagekosten zijn alleen inbegrepen wanneer dit expliciet staat vermeld.',
      {
        size: 8.5,
        lineHeight: 4.2,
        color: [75, 75, 75]
      }
    );

    y += 5;

    write('Aanbetaling bij voertuigen', {
      size: 10,
      style: 'bold'
    });

    write(
      'Deze mogelijkheid geldt uitsluitend bij de aankoop van een voertuig en niet bij een bestelling van alleen accessoires, onderdelen of helmen.',
      {
        size: 9,
        lineHeight: 4.5,
        style: 'bold',
        color: [45, 45, 45]
      }
    );

    y += 2;

    write(
      'Wilt u een voertuig bestellen, maar liever niet het volledige bedrag direct via de webshop betalen? Dan kunt u kiezen voor een aanbetaling.',
      {
        size: 9,
        lineHeight: 4.5,
        color: [45, 45, 45]
      }
    );

    y += 2;

    write(
      'Mail deze pdf naar de winkel van uw voorkeur, inclusief de volgende gegevens:',
      {
        size: 9,
        lineHeight: 4.5,
        style: 'bold',
        color: [45, 45, 45]
      }
    );

    write('• Naam', {
      x: left + 5,
      maxWidth: contentWidth - 5,
      size: 9,
      lineHeight: 4.5,
      color: [45, 45, 45]
    });

    write('• Adres', {
      x: left + 5,
      maxWidth: contentWidth - 5,
      size: 9,
      lineHeight: 4.5,
      color: [45, 45, 45]
    });

    write('• Telefoonnummer', {
      x: left + 5,
      maxWidth: contentWidth - 5,
      size: 9,
      lineHeight: 4.5,
      color: [45, 45, 45]
    });

    write('• E-mailadres', {
      x: left + 5,
      maxWidth: contentWidth - 5,
      size: 9,
      lineHeight: 4.5,
      color: [45, 45, 45]
    });

    y += 2;

    write(
      'Wij stellen vervolgens de factuur voor u op en sturen deze per e-mail toe. Daarbij ontvangt u ook een betaallink voor een aanbetaling van € 250,-.',
      {
        size: 9,
        lineHeight: 4.5,
        color: [45, 45, 45]
      }
    );

    y += 2;

    write(
      'Het resterende bedrag betaalt u wanneer het voertuig bij u wordt afgeleverd. De levering vindt doorgaans binnen één week plaats.',
      {
        size: 9,
        lineHeight: 4.5,
        style: 'bold',
        color: [45, 45, 45]
      }
    );
  /*
 * Contactgegevens.
 */
y += 4;

const contactBoxHeight = 31;

/*
 * Controleer eerst of het volledige blok nog op de pagina past.
 */
space(contactBoxHeight + 8);

const contactBoxY = y;
const dividerX = left + contentWidth / 2;

const leftColumnX = left + 5;
const rightColumnX = dividerX + 5;

doc.setFillColor(248, 248, 248);

doc.roundedRect(
  left,
  contactBoxY,
  contentWidth,
  contactBoxHeight,
  2,
  2,
  'F'
);

/*
 * Titel.
 */
doc.setTextColor(0, 0, 0);
doc.setFont('helvetica', 'bold');
doc.setFontSize(10);

doc.text(
  'Contactgegevens',
  left + 5,
  contactBoxY + 7
);

/*
 * Horizontale scheidingslijn onder de titel.
 */
doc.setDrawColor(220, 220, 220);

doc.line(
  left + 5,
  contactBoxY + 10,
  left + contentWidth - 5,
  contactBoxY + 10
);

/*
 * Verticale scheidingslijn tussen beide winkels.
 */
doc.line(
  dividerX,
  contactBoxY + 14,
  dividerX,
  contactBoxY + contactBoxHeight - 5
);

/*
 * Rotterdam.
 */
doc.setFont('helvetica', 'bold');
doc.setFontSize(9);

doc.text(
  'Winkel Rotterdam',
  leftColumnX,
  contactBoxY + 17
);

doc.setFont('helvetica', 'normal');
doc.setFontSize(8.3);

doc.text(
  'rotterdam@j2scooters.nl',
  leftColumnX,
  contactBoxY + 23
);

doc.text(
  '085 744 45 98',
  leftColumnX,
  contactBoxY + 28
);

/*
 * Utrecht.
 */
doc.setFont('helvetica', 'bold');
doc.setFontSize(9);

doc.text(
  'Winkel Utrecht',
  rightColumnX,
  contactBoxY + 17
);

doc.setFont('helvetica', 'normal');
doc.setFontSize(8.3);

doc.text(
  'utrecht@j2scooters.nl',
  rightColumnX,
  contactBoxY + 23
);

doc.text(
  '085 744 45 98',
  rightColumnX,
  contactBoxY + 28
);

/*
 * Ruimte na het contactblok.
 */
y =
  contactBoxY +
  contactBoxHeight +
  6;
    /*
     * Page footers.
     */
    const pageCount =
      doc.getNumberOfPages();

    for (
      let page = 1;
      page <= pageCount;
      page += 1
    ) {
      doc.setPage(page);

      doc.setDrawColor(
        220,
        220,
        220
      );

      doc.line(
        left,
        pageHeight - 11,
        pageWidth - right,
        pageHeight - 11
      );

      doc.setFont(
        'helvetica',
        'normal'
      );

      doc.setFontSize(8);

      doc.setTextColor(
        100,
        100,
        100
      );

      doc.text(
        CONFIG.website,
        left,
        pageHeight - 6
      );

      doc.text(
        `Pagina ${page} van ${pageCount}`,
        pageWidth - right,
        pageHeight - 6,
        {
          align: 'right'
        }
      );
    }

    doc.setProperties({
      title:
        `Winkelwagenoverzicht - ${ref}`,

      author:
        CONFIG.shopName,

      creator:
        CONFIG.shopName
    });

    const date = [
      now.getFullYear(),

      String(
        now.getMonth() + 1
      ).padStart(2, '0'),

      String(
        now.getDate()
      ).padStart(2, '0')
    ].join('-');

    doc.save(
      `J2-winkelwagen-${date}.pdf`
    );
  }

  async function download(
    button
  ) {
    if (generating) return;

    generating = true;

    setBusy(button, true);

    try {
      const [
        cart,
        JsPDF,
        logo
      ] = await Promise.all([
        getCart(),
        loadJsPdf(),
        loadLogo()
      ]);

      if (
        !cart.items?.length
      ) {
        throw new Error(
          'De winkelwagen is leeg.'
        );
      }

      await addCompareAtPrices(
        cart
      );

      makePdf(
        cart,
        JsPDF,
        logo
      );
    } catch (error) {
      console.error(
        '[J2 Cart PDF]',
        error
      );

      alert(
        error?.message ||
          'De PDF kon niet worden gemaakt. Probeer het opnieuw.'
      );
    } finally {
      setBusy(
        button,
        false
      );

      generating = false;
    }
  }

  window.J2CartPDF = {
    download
  };

  /*
   * Event delegation is required
   * because UpCart redraws its HTML.
   */
  document.addEventListener(
    'click',
    (event) => {
      const button =
        event.target.closest(
          CONFIG.button
        );

      if (!button) return;

      event.preventDefault();

      download(button);
    }
  );
})();